import { isNil, last } from 'lodash';
import { ISchemaSummary } from './SchemaExplorerPage.types';

export const getSchemaSummary = (
  uri: string,
  displayName: string,
  objectType: ISchemaSummary['objectType'],
): ISchemaSummary => {
  return {
    uri,
    name: last(uri.split('/'))!,
    objectType,
    displayName,
  };
};

export const resolveSchema = (schema: any, rootSchema: any) => {
  const ref = schema.$ref;
  if (ref && ref.startsWith('#/definitions/')) {
    const name = ref.replace('#/definitions/', '');
    if (typeof rootSchema === 'object' && 'definitions' in rootSchema) {
      const definition = rootSchema['definitions'][name];
      if (definition) {
        return definition;
      }
    }
  }
  return schema;
};

export const generateSchemaValue = (rootSchema: any): any => {
  const obj: Record<string, any> = {};

  const getSchemaType = (schema: any, depth: number): string | null => {
    if (Array.isArray(schema.type)) {
      if (depth <= 0) {
        for (const type of schema.type) {
          if (type !== 'null') {
            return type;
          }
        }
      }

      if (schema.type.includes('null')) {
        return 'null';
      }

      if (schema.type.length) {
        return schema.type[0];
      }

      return null;
    }

    if (schema.type) {
      return schema.type;
    }

    return null;
  };

  const getSchemaValue = (schema: any, depth: number): any => {
    const schemaType = getSchemaType(schema, depth);

    if (typeof schema === 'object') {
      if (Object.keys(schema).length === 0) {
        return {};
      }

      if (!isNil(schema['default'])) {
        return schema['default'];
      }
    }

    if (schemaType) {
      if (depth > 0) {
        return undefined;
      }

      switch (schemaType) {
        case 'array':
          return [];
        case 'string':
          if (schema.format === 'guid') {
            return '00000000-0000-0000-0000-000000000000';
          }

          if (Array.isArray(schema.enum)) {
            return schema.enum[0];
          }

          return '';
        case 'number':
        case 'integer':
          return 0;
        case 'boolean':
          return false;
        case 'object': {
          const value: Record<string, any> = {};
          if (schema.properties && typeof schema.properties === 'object') {
            for (const key in schema.properties) {
              value[key] = getSchemaValue(
                resolveSchema(schema.properties[key], rootSchema),
                depth + 1,
              );
            }
          }
          return value;
        }
      }
    }

    if ('anyOf' in schema && Array.isArray(schema.anyOf)) {
      const values = schema.anyOf.map((item: any) =>
        getSchemaValue(resolveSchema(item, rootSchema), depth),
      );
      if (values.length > 0) {
        if (depth <= 0) {
          const value = values.find((v: any) => v !== null);
          if (value) {
            return value;
          }
        }

        if (values.includes(null)) {
          return null;
        }

        return values[0];
      }
    }

    if ('oneOf' in schema && Array.isArray(schema.oneOf)) {
      const values = schema.oneOf.map((item: any) =>
        getSchemaValue(resolveSchema(item, rootSchema), depth),
      );
      if (values.length > 0) {
        if (depth <= 0) {
          const value = values.find((v: any) => v !== null);
          if (value) {
            return value;
          }
        }

        if (values.includes(null)) {
          return null;
        }

        return values[0];
      }
    }

    return undefined;
  };

  if (rootSchema.properties && typeof rootSchema.properties === 'object') {
    for (const key in rootSchema.properties) {
      obj[key] = getSchemaValue(resolveSchema(rootSchema.properties[key], rootSchema), 0);
    }
  }

  return obj;
};

export const getPropertiesSchema = (schema: any) => {
  if (typeof schema?.properties?.properties === 'object') {
    let propertiesSchema = resolveSchema(schema.properties.properties, schema);

    if ('oneOf' in propertiesSchema && Array.isArray(propertiesSchema.oneOf)) {
      for (const item of propertiesSchema.oneOf) {
        if (item.type === 'null') continue;
        const resolved = resolveSchema(item, schema);
        if (resolved.type === 'object') return resolved;
      }
    }
  }

  return null;
};
